// components/screeners/MomentumScreener.jsx
import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { nseIndicesOptions } from "../Helper/Helperdata";
import useLivePrices from "../../hooks/useLivePrices";
import useMomentum from "../../hooks/useMomentum";
import { useExport } from "../../hooks/useExport";

// ── Your existing frontend index list ───────────────────────────
const INDEX_OPTIONS = [
  { value: "NIFTY 50", label: "NIFTY 50" },
  { value: "NIFTY BANK", label: "NIFTY BANK" },
  { value: "NIFTY IT", label: "NIFTY IT" },
  { value: "NIFTY AUTO", label: "NIFTY AUTO" },
  { value: "NIFTY PHARMA", label: "NIFTY PHARMA" },
];

// ── Table column config ─────────────────────────────────────────
// const COLUMNS = [
//   { key: "symbol", label: "NAME", width: 130, sticky: true },
//   { key: "ltp", label: "LTP", width: 85, align: "right" },
//   { key: "change_percent", label: "CHG %", width: 70, align: "right" },
//   { key: "low", label: "LOW", width: 70, align: "right" },
//   { key: "high", label: "HIGH", width: 70, align: "right" },
//   { key: "rsi", label: "RSI", width: 55, align: "right" },
//   { key: "volume", label: "VOLUME", width: 90, align: "right" },
//   { key: "avg_vol_20d", label: "AVG VOL (20D)", width: 105, align: "right" },
//   { key: "rvol", label: "RVOL", width: 60, align: "right" },
//   { key: "high_52w", label: "52W HIGH", width: 90, align: "right" },
//   { key: "dist_from_high", label: "DIST %", width: 65, align: "right" },

//   { key: "sma_50", label: "SMA 50", width: 85, align: "right" },
//   { key: "sma_100", label: "SMA 100", width: 85, align: "right" },
//   { key: "ema_20", label: "EMA 20", width: 85, align: "right" },
//   { key: "macd", label: "MACD", width: 65, align: "right" },
//   { key: "histogram", label: "HIST", width: 60, align: "right" },
//   { key: "vwap_signal", label: "VWAP", width: 65, align: "center" },
//   { key: "prediction", label: "PREDICT", width: 72, align: "center" },
//   { key: "momentum_score", label: "SCORE", width: 70, align: "center" },
//   { key: "momentum_signal", label: "SIGNAL", width: 105, align: "center" },
// ];
const COLUMNS = [
  {
    key: "symbol",
    label: "NAME",
    width: 130,
    sticky: true,
    tooltip: "Stock symbol / company ticker",
  },
  {
    key: "ltp",
    label: "LTP",
    width: 85,
    align: "right",
    tooltip: "Last Traded Price — latest available market price",
  },
  {
    key: "prev_close",
    label: "PREV_CLOSE",
    width: 85,
    align: "right",
    tooltip: "Previous day Closing Price",
  },
  {
    key: "change_percent",
    label: "CHG %",
    width: 70,
    align: "right",
    tooltip: "Percentage change from previous closing price",
  },
  {
    key: "low",
    label: "LOW",
    width: 70,
    align: "right",
    tooltip: "Today's intraday low price",
  },
  {
    key: "high",
    label: "HIGH",
    width: 70,
    align: "right",
    tooltip: "Today's intraday high price",
  },
  {
    key: "rsi",
    label: "RSI",
    width: 55,
    align: "right",
    tooltip:
      "Relative Strength Index. Above 60 shows stronger momentum; above 70 may indicate overbought conditions.",
  },
  {
    key: "volume",
    label: "VOLUME",
    width: 90,
    align: "right",
    tooltip: "Total shares traded today",
  },
  {
    key: "macd_signal", // ← key now matches the backend field
    label: "Signal Line",
    width: 90,
    align: "right",
    tooltip: "MACD Signal Line (9-period EMA of MACD)",
  },
  {
    key: "vwap_signal",
    label: "VWAP",
    width: 65,
    align: "center",
    tooltip:
      "VWAP signal. Bullish when price is trading favorably above Volume Weighted Average Price; bearish when below.",
  },
  {
    key: "prediction",
    label: "PREDICT",
    width: 72,
    align: "center",
    tooltip:
      "Technical trend prediction generated from the indicator conditions.",
  },
  {
    key: "momentum_score",
    label: "SCORE",
    width: 70,
    align: "center",
    tooltip:
      "Combined momentum score based on the screener's technical conditions. Higher score means stronger confluence.",
  },
  {
    key: "momentum_signal",
    label: "SIGNAL",
    width: 105,
    align: "center",
    tooltip:
      "Final momentum classification such as STRONG BUY, BUY, WATCH or SKIP.",
  },
  {
    key: "avg_vol_20d",
    label: "AVG VOL (20D)",
    width: 105,
    align: "right",
    tooltip: "Average daily trading volume over the previous 20 trading days",
  },
  {
    key: "rvol",
    label: "RVOL",
    width: 60,
    align: "right",
    tooltip:
      "Relative Volume = Current Volume compared with normal/average volume. Above 1× means above-normal activity.",
  },
  {
    key: "high_52w",
    label: "52W HIGH",
    width: 90,
    align: "right",
    tooltip: "Highest price reached by the stock during the last 52 weeks",
  },
  {
    key: "dist_from_high",
    label: "DIST %",
    width: 65,
    align: "right",
    tooltip:
      "Distance from 52-week high. Shows how far the current price is below its 52W High. Lower is closer to the high: 0–5% near high, 5–10% moderately close, >10% farther away.",
  },

  {
    key: "sma_50",
    label: "SMA 50",
    width: 85,
    align: "right",
    tooltip: "50-day Simple Moving Average",
  },
  {
    key: "sma_100",
    label: "SMA 100",
    width: 85,
    align: "right",
    tooltip: "100-day Simple Moving Average",
  },
  {
    key: "ema_20",
    label: "EMA 20",
    width: 85,
    align: "right",
    tooltip:
      "20-day Exponential Moving Average. Gives more weight to recent prices.",
  },
  {
    key: "macd",
    label: "MACD",
    width: 65,
    align: "right",
    tooltip:
      "Moving Average Convergence Divergence value used to measure trend momentum.",
  },
  {
    key: "histogram",
    label: "HIST",
    width: 60,
    align: "right",
    tooltip:
      "MACD Histogram = MACD − Signal Line. Positive values indicate bullish momentum; negative values indicate bearish momentum.",
  },
];
// const COLUMNS = [
//   { key: "symbol", label: "NAME", width: 130, sticky: true },
//   { key: "ltp", label: "LTP", width: 85, align: "right" },
//   { key: "changePct", label: "CHG %", width: 70, align: "right" }, // was change_percent
//   { key: "low", label: "LOW", width: 70, align: "right" },
//   { key: "high", label: "HIGH", width: 70, align: "right" },
//   { key: "rsi", label: "RSI", width: 55, align: "right" },
//   { key: "volume", label: "VOLUME", width: 90, align: "right" },
//   { key: "avg_vol_20d", label: "AVG VOL (20D)", width: 105, align: "right" },
//   { key: "rvol", label: "RVOL", width: 60, align: "right" },
//   { key: "high_52w", label: "52W HIGH", width: 90, align: "right" },
//   { key: "dist_from_high", label: "DIST %", width: 65, align: "right" },
//   { key: "sma50", label: "SMA 50", width: 85, align: "right" },
//   { key: "sma100", label: "SMA 100", width: 85, align: "right" },
//   { key: "ema20", label: "EMA 20", width: 85, align: "right" }, // was ema_20
//   { key: "macd", label: "MACD", width: 65, align: "right" },
//   { key: "histogram", label: "HIST", width: 60, align: "right" },
//   { key: "vwap_signal", label: "VWAP", width: 65, align: "center" },
//   { key: "prediction", label: "PREDICT", width: 72, align: "center" }, // comma fixed
//   { key: "score", label: "SCORE", width: 70, align: "center" }, // was momentum_score
//   { key: "status", label: "SIGNAL", width: 105, align: "center" }, // was momentum_signal
// ];

// ── Formatters ──────────────────────────────────────────────────
const formatPrice = (v) =>
  v != null
    ? `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

const formatVol = (v) => {
  if (v == null) return "—";
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return String(v);
};

const formatChange = (v) => {
  if (v == null) return "—";
  const color = v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#64748b";
  return (
    <span style={{ color, fontWeight: 500 }}>
      {v > 0 ? "+" : ""}
      {v.toFixed(2)}%
    </span>
  );
};

const formatRsi = (v) => {
  if (v == null) return "—";
  const color =
    v >= 70 ? "#dc2626" : v >= 60 ? "#16a34a" : v >= 40 ? "#64748b" : "#3b82f6";
  return <span style={{ color, fontWeight: 500 }}>{v.toFixed(1)}</span>;
};

const formatRvol = (v) => {
  if (v == null) return "—";
  const color = v >= 2 ? "#16a34a" : v >= 1.5 ? "#ca8a04" : "#64748b";
  return <span style={{ color, fontWeight: 600 }}>{v.toFixed(2)}×</span>;
};

const formatDist = (v) => {
  if (v == null) return "—";
  const color = v <= 5 ? "#16a34a" : v <= 10 ? "#ca8a04" : "#dc2626";
  return <span style={{ color }}>{v.toFixed(1)}%</span>;
};

const formatMacd = (v) => {
  if (v == null) return "—";
  const color = v > 0 ? "#16a34a" : v < 0 ? "#dc2626" : "#64748b";
  return <span style={{ color }}>{v.toFixed(2)}</span>;
};

const SignalBadge = ({ value }) => {
  if (!value) return <span>—</span>;
  const map = {
    Bullish: { bg: "#dcfce7", color: "#166534" },
    Bearish: { bg: "#fee2e2", color: "#991b1b" },
    Neutral: { bg: "#f1f5f9", color: "#64748b" },
  };
  const s = map[value] || map.Neutral;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {value}
    </span>
  );
};

const ScoreBar = ({ score }) => {
  if (score == null) return <span>—</span>;
  const color =
    score >= 80
      ? "#16a34a"
      : score >= 60
        ? "#22c55e"
        : score >= 40
          ? "#ca8a04"
          : "#dc2626";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div
        style={{
          width: 36,
          height: 5,
          background: "#e2e8f0",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${score}%`,
            height: "100%",
            background: color,
            borderRadius: 3,
          }}
        />
      </div>
      <span style={{ color, fontWeight: 600, fontSize: 12 }}>{score}</span>
    </div>
  );
};

const MomentumSignalBadge = ({ value }) => {
  if (!value) return <span>—</span>;
  const map = {
    "STRONG BUY": { bg: "#16a34a", color: "#fff" },
    BUY: { bg: "#dcfce7", color: "#166534" },
    WATCH: { bg: "#fef3c7", color: "#92400e" },
    SKIP: { bg: "#f1f5f9", color: "#94a3b8" },
  };
  const s = map[value] || map.SKIP;
  return (
    <span
      style={{
        background: s.bg,
        color: s.color,
        padding: "3px 8px",
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </span>
  );
};

// ── Cell renderer ───────────────────────────────────────────────
const renderCell = (col, row) => {
  const v = row[col.key];

  switch (col.key) {
    case "symbol":
      return (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{row.symbol}</div>
        </div>
      );
    case "ltp":
    case "prev_close":
    case "high":
    case "low":
    case "high_52w":
    case "ema_20":
    case "sma_50":
    case "sma_100":
    case "high_52w":
    case "ema_20":
      return <span style={{ fontSize: 12.5 }}>{formatPrice(v)}</span>;
    case "change_percent":
      return formatChange(v);
    case "rsi":
      return formatRsi(v);
    case "volume":
    case "signal_line": {
      const sig = row.macd_signal;
      if (sig == null || isNaN(Number(sig))) {
        return <span style={{ fontSize: 12.5 }}>—</span>;
      }
      const n = Number(sig);
      const color = n > 0 ? "#16a34a" : n < 0 ? "#dc2626" : "#64748b";
      return <span style={{ fontSize: 12.5, color }}>{n.toFixed(2)}</span>;
    }
    case "avg_vol_20d":
      return <span style={{ fontSize: 12.5 }}>{formatVol(v)}</span>;
    case "rvol":
      return formatRvol(v);
    case "dist_from_high":
      return formatDist(v);
    case "macd":

    case "histogram":
      return formatMacd(v);
    case "vwap_signal":
    case "prediction":
      return <SignalBadge value={v} />;
    case "momentum_score":
      return <ScoreBar score={v} />;
    case "momentum_signal":
      return <MomentumSignalBadge value={v} />;

    default:
      return <span style={{ fontSize: 12.5 }}>{v ?? "—"}</span>;
  }
};

// const renderCell = (col, row) => {
//   const v = row[col.key];

//   switch (col.key) {
//     case "symbol":
//       return (
//         <div>
//           <div style={{ fontWeight: 600, fontSize: 13 }}>{row.symbol}</div>
//         </div>
//       );

//     case "ltp":
//     case "high":
//     case "low":
//     case "high_52w":
//     case "ema20":
//     case "ema50":
//     case "ema200":
//     case "sma50":
//     case "sma100":
//       return <span style={{ fontSize: 12.5 }}>{formatPrice(v)}</span>;

//     case "changePct":
//       return formatChange(v);

//     case "rsi":
//       return formatRsi(v);

//     case "volume":
//     case "avg_vol_20d":
//       return <span style={{ fontSize: 12.5 }}>{formatVol(v)}</span>;

//     case "rvol":
//       return formatRvol(v);

//     case "dist_from_high":
//       return formatDist(v);

//     case "macd":
//     case "histogram":
//       return formatMacd(v);

//     case "vwap_signal":
//     case "prediction":
//       return <SignalBadge value={v} />;

//     case "score":
//       return <ScoreBar score={v} />;

//     case "status":
//       return <MomentumSignalBadge value={v} />;

//     default:
//       return <span style={{ fontSize: 12.5 }}>{v ?? "—"}</span>;
//   }
// };
// const renderCell = (col, row) => {
//   const v = row[col.key];

//   switch (col.key) {
//     case "symbol":
//       return (
//         <div>
//           <div style={{ fontWeight: 600, fontSize: 13 }}>{row.symbol}</div>
//         </div>
//       );
//     case "ltp":
//     case "high_52w":
//     case "ema_20":
//     case "sma50":
//     case "sma100":
//       return <span style={{ fontSize: 12.5 }}>{formatPrice(v)}</span>;
//     case "change_percent":
//       return formatChange(v);
//     case "rsi":
//       return formatRsi(v);
//     case "volume":
//     case "avg_vol_20d":
//       return <span style={{ fontSize: 12.5 }}>{formatVol(v)}</span>;
//     case "rvol":
//       return formatRvol(v);
//     case "dist_from_high":
//       return formatDist(v);
//     case "macd":
//     case "histogram":
//       return formatMacd(v);
//     case "vwap_signal":
//     case "prediction":
//       return <SignalBadge value={v} />;
//     case "momentum_score":
//       return <ScoreBar score={v} />;
//     case "momentum_signal":
//       return <MomentumSignalBadge value={v} />;
//     case "high":
//     case "low":
//       return formatPrice(row[col.key]);
//     default:
//       return <span style={{ fontSize: 12.5 }}>{v ?? "—"}</span>;
//   }
// };

// ── Filter criteria (expanded row) ──────────────────────────────
const FilterBadges = ({ filters }) => {
  if (!filters) return null;
  const items = [
    { key: "rsi", label: "RSI ≥ 60" },
    { key: "volume", label: "RVOL ≥ 2×" },
    { key: "nearHigh", label: "Near 52W High" },
    { key: "aboveEma", label: "Above EMA20" },
    { key: "macdBullish", label: "MACD +ve" },
    { key: "vwapBullish", label: "VWAP Bull" },
    { key: "utBullish", label: "UT Bot" },
    { key: "bbaweBullish", label: "BBAWE" },
  ];
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {items.map((it) => (
        <span
          key={it.key}
          style={{
            fontSize: 10,
            padding: "2px 6px",
            borderRadius: 3,
            fontWeight: 500,
            background: filters[it.key] ? "#dcfce7" : "#fee2e2",
            color: filters[it.key] ? "#166534" : "#991b1b",
          }}
        >
          {filters[it.key] ? "✓" : "✗"} {it.label}
        </span>
      ))}
    </div>
  );
};
const HeaderTooltip = ({ text }) => {
  const [show, setShow] = useState(false);

  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 4,
      }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={(e) => e.stopPropagation()}
    >
      <span
        style={{
          fontSize: 11,
          cursor: "help",
          color: "#94a3b8",
          lineHeight: 1,
        }}
      >
        ⓘ
      </span>

      {show && (
        <span
          style={{
            position: "absolute",

            // ✅ OPEN BELOW THE HEADER
            top: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",

            width: 240,
            padding: "10px 12px",

            // ✅ WHITE TOOLTIP
            background: "#ffffff",
            color: "#334155",

            border: "1px solid #e2e8f0",
            borderRadius: 8,

            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.5,
            whiteSpace: "normal",
            textAlign: "left",

            boxShadow: "0 6px 18px rgba(15, 23, 42, 0.12)",

            zIndex: 9999,
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

// ── MAIN COMPONENT ──────────────────────────────────────────────

const MomentumScreener = () => {
  // const [selectedIndex, setSelectedIndex] = useState("NIFTY 50");  //earlier
  const [selectedIndex, setSelectedIndex] = useState("ALL");
  const [sortKey, setSortKey] = useState("momentum_score");
  const [sortDir, setSortDir] = useState("desc");
  const [filterSignal, setFilterSignal] = useState("ALL");
  const { exportCSV, exportExcel, exporting } = useExport();

  const [changeFilter, setChangeFilter] = useState("ALL");
  const [volumeFilter, setVolumeFilter] = useState("ALL");

  const [openFilter, setOpenFilter] = useState(null); // e.g. "change"
  //useref
  const changeRef = useRef(null);
  const volumeRef = useRef(null);
  const [changeMin, setChangeMin] = useState("0.5");
  const [changeMax, setChangeMax] = useState("");

  //new volume change
  const [volumeMin, setVolumeMin] = useState("");
  const [volumeMax, setVolumeMax] = useState("");

  const [expandedRow, setExpandedRow] = useState(null);
  const { prices: livePrices, subscribeSymbols } = useLivePrices();
  // 60s auto-refresh during market hours — set 0 to disable
  const { data, stats, loading, error, lastUpdated, refresh } = useMomentum(
    selectedIndex,
    60000,
  );
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) return;

    const symbols = data.map((row) => row?.symbol).filter(Boolean);

    if (symbols.length > 0) {
      subscribeSymbols(symbols);
    }
  }, [data, subscribeSymbols]);
  console.log("data from user", data);
  console.log("stats from user", stats);
  const handleSort = useCallback(
    (key) => {
      if (sortKey === key) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortKey(key);
        setSortDir("desc");
      }
    },
    [sortKey],
  );

  const openStockDetail = (row) => {
    if (!row?.symbol || !row?.security_id) return;
    window.open(`/stockdetail/${row.symbol}/${row.security_id}`, "_blank");
  };

  const liveData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.map((row) => {
      const symbol = String(row?.symbol || "")
        .trim()
        .toUpperCase();

      const live = livePrices?.[symbol];

      const backendLtp = Number(row?.ltp);
      const prevClose = Number(row?.prev_close);

      const calculatedBackendPercent =
        Number.isFinite(backendLtp) &&
        Number.isFinite(prevClose) &&
        prevClose > 0
          ? ((backendLtp - prevClose) / prevClose) * 100
          : null;

      const apiPercent = Number(
        row?.change_percent ?? row?.percent_change ?? row?.pChange,
      );

      const backendPercent =
        calculatedBackendPercent ??
        (Number.isFinite(apiPercent) ? apiPercent : null);

      // Use this condition only while the live feed is actually active.
      // A stored `live` object alone does not prove the socket is active.
      const useLivePercent = live?.percentChange != null;

      const changePercent = useLivePercent
        ? Number(live.percentChange)
        : backendPercent;

      // Backend values = fallback
      // const backendLtp = Number(row?.ltp);
      const backendVolume = Number(
        row?.volume ?? row?.totalTradedVolume ?? row?.total_traded_volume ?? 0,
      );

      // WebSocket values = priority
      const ltp = live?.ltp != null ? Number(live.ltp) : backendLtp;

      const volume = live?.volume != null ? Number(live.volume) : backendVolume;

      const high = live?.high != null ? Number(live.high) : row?.high;

      const low = live?.low != null ? Number(live.low) : row?.low;

      const open = live?.open != null ? Number(live.open) : row?.open;

      // const backendPercent = ((ltp - row?.prev_close) / row?.prev_close) * 100;

      // const changePercent =
      //   live?.percentChange != null
      //     ? Number(live?.percentChange)
      //     : Number(backendPercent);

      // Optional: make RVOL live using live volume
      const avgVolume20 = Number(row?.avg_vol_20d ?? 0);

      const liveRvol =
        avgVolume20 > 0 && Number.isFinite(volume)
          ? Number((volume / avgVolume20).toFixed(2))
          : row?.rvol;

      return {
        ...row,

        // LIVE VALUES
        ltp,
        volume,
        high,
        low,
        open,
        change_percent: changePercent,

        // optional live calculation
        rvol: liveRvol,

        // useful for debugging/UI
        isLive: !!live,
      };
    });
  }, [data, livePrices]);
  // const sortedData = useMemo(() => {
  //   let filtered = [...data];

  //   if (filterSignal !== "ALL") {
  //     filtered = filtered.filter((r) => r.momentum_signal === filterSignal);
  //   }

  //   filtered.sort((a, b) => {
  //     const av = a[sortKey];
  //     const bv = b[sortKey];
  //     if (av == null && bv == null) return 0;
  //     if (av == null) return 1;
  //     if (bv == null) return -1;
  //     if (typeof av === "string")
  //       return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
  //     return sortDir === "asc" ? av - bv : bv - av;
  //   });

  //   return filtered;
  // }, [data, sortKey, sortDir, filterSignal]);
  const sortedData = useMemo(() => {
    let filtered = [...liveData];

    // Existing momentum signal filter
    if (filterSignal !== "ALL") {
      filtered = filtered.filter((row) => row.momentum_signal === filterSignal);
    }

    // Percentage-change filter
    // if (changeFilter !== "ALL") {
    //   filtered = filtered.filter((row) => {
    //     const percentChange = Number(
    //       row.percent_change ?? row.pChange ?? row.change_percent ?? 0,
    //     );

    //     if (changeFilter === "ABOVE_1") {
    //       return percentChange >= 1;
    //     }

    //     if (changeFilter === "BELOW_1") {
    //       return percentChange < 1;
    //     }

    //     return true;
    //   });
    // }
    // Percentage-change range filter
    const cMin = changeMin === "" ? null : Number(changeMin);
    const cMax = changeMax === "" ? null : Number(changeMax);

    if (cMin !== null || cMax !== null) {
      filtered = filtered.filter((row) => {
        const percentChange = Number(
          row.percent_change ?? row.pChange ?? row.change_percent ?? 0,
        );
        if (cMin !== null && percentChange < cMin) return false;
        if (cMax !== null && percentChange > cMax) return false;
        return true;
      });
    }

    // Volume filter
    // if (volumeFilter !== "ALL") {
    //   filtered = filtered.filter((row) => {
    //     const volume = Number(
    //       row.volume ?? row.totalTradedVolume ?? row.total_traded_volume ?? 0,
    //     );

    //     if (volumeFilter === "ABOVE_1L") {
    //       return volume >= 100000;
    //     }

    //     if (volumeFilter === "ABOVE_5L") {
    //       return volume >= 500000;
    //     }

    //     if (volumeFilter === "ABOVE_10L") {
    //       return volume >= 1000000;
    //     }

    //     return true;
    //   });
    // }
    // Volume range filter (inputs are in lakhs)
    const vMin = volumeMin === "" ? null : Number(volumeMin) * 100000;
    const vMax = volumeMax === "" ? null : Number(volumeMax) * 100000;

    if (vMin !== null || vMax !== null) {
      filtered = filtered.filter((row) => {
        const volume = Number(
          row.volume ?? row.totalTradedVolume ?? row.total_traded_volume ?? 0,
        );
        if (vMin !== null && volume < vMin) return false;
        if (vMax !== null && volume > vMax) return false;
        return true;
      });
    }

    // Existing sorting
    filtered.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      if (typeof av === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }

      return sortDir === "asc" ? av - bv : bv - av;
    });

    return filtered;
  }, [
    liveData,
    sortKey,
    sortDir,
    filterSignal,
    changeMin,
    changeMax,
    volumeMin,
    volumeMax,
  ]);

  //handle
  useEffect(() => {
    if (!openFilter) return;

    const handleClickOutside = (e) => {
      const inChange = changeRef.current?.contains(e.target);
      const inVolume = volumeRef.current?.contains(e.target);
      if (!inChange && !inVolume) {
        setOpenFilter(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openFilter]);
  const EXPORT_COLUMNS = [
    { header: "Symbol", key: "symbol", width: 15 },
    { header: "LTP", key: "ltp", width: 12 },
    { header: "% Change", key: "percent_change", width: 12 },
    { header: "High", key: "high", width: 12 },
    { header: "Low", key: "low", width: 12 },
    { header: "Volume", key: "volume", width: 14 },
    { header: "MACD Signal", key: "macd_signal", width: 16 },
    { header: "RSI", key: "rsi", width: 10 },
    { header: "RVOL", key: "rvol", width: 10 },
    { header: "Momentum Score", key: "momentum_score", width: 16 },
    { header: "Momentum Signal", key: "momentum_signal", width: 16 },
    { header: "UT Signal", key: "ut_signal", width: 12 },
    { header: "BBAWE Signal", key: "bbawe_signal", width: 14 },
    { header: "Smart Money", key: "smart_money", width: 14 },
    { header: "52W High", key: "high_52w", width: 12 },
    { header: "52W Low", key: "low_52w", width: 12 },
  ];

  const buildExportRows = () =>
    sortedData.map((row) => ({
      symbol: row.symbol ?? "",
      ltp: row.ltp ?? row.close ?? "",
      close: row.prev_close ?? "",
      percent_change: row.percent_change ?? row.pChange ?? "",
      high: row.high ?? "", // ← add
      low: row.low ?? "",
      volume: row.volume ?? row.totalTradedVolume ?? "",
      macd_signal: row?.macd_signal ?? "",
      rsi: row.rsi ?? "",
      rvol: row.rvol ?? "",
      momentum_score: row.momentum_score ?? "",
      momentum_signal: row.momentum_signal ?? "",
      ut_signal: row.ut_signal ?? "",
      bbawe_signal: row.bbawe_signal ?? "",
      smart_money: row.smart_money ?? "",
      high_52w: row.high_52w ?? "",
      low_52w: row.low_52w ?? "",
    }));
  const handleDownloadCSV = () =>
    exportCSV({
      rows: buildExportRows(),
      columns: EXPORT_COLUMNS,
      filename: `${selectedIndex}_momentum${filterSignal !== "ALL" ? `_${filterSignal.toLowerCase().replace(/ /g, "_")}` : ""}`,
      sheetName: selectedIndex,
    });

  const handleDownloadExcel = () =>
    exportExcel({
      rows: buildExportRows(),
      columns: EXPORT_COLUMNS,
      filename: `${selectedIndex}_momentum${filterSignal !== "ALL" ? `_${filterSignal.toLowerCase().replace(/ /g, "_")}` : ""}`,
      sheetName: selectedIndex,
    });
  return (
    <div>
      {/* ── Header row ──────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button
            onClick={() => window.history.back()}
            style={{
              background: "none",
              border: "1px solid #e2e8f0",
              borderRadius: 8,
              width: 36,
              height: 36,
              cursor: "pointer",
              fontSize: 16,
            }}
          >
            ←
          </button>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
              Momentum Screener
            </h2>
            <p style={{ margin: "2px 0 0", fontSize: 13, color: "#64748b" }}>
              RSI + Volume surge + Price near 52W High across index stocks
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select
            value={selectedIndex}
            onChange={(e) => {
              setSelectedIndex(e.target.value);
              setExpandedRow(null);
            }}
            style={{
              padding: "8px 32px 8px 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              fontSize: 13,
              fontWeight: 500,
              background: "white",
              cursor: "pointer",
            }}
          >
            <option value="ALL">All Stocks</option>
            {nseIndicesOptions.map((opt) => (
              // <option key={opt.value} value={opt.value}>
              //   {opt}
              // </option>
              <option key={opt.value || opt} value={opt.value || opt}>
                {opt.label || opt}
              </option>
            ))}
          </select>

          <button
            onClick={refresh}
            disabled={loading}
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "white",
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: 16,
              opacity: loading ? 0.5 : 1,
            }}
            title="Refresh"
          >
            ↻
          </button>
          <button
            onClick={handleDownloadCSV}
            disabled={exporting}
            style={{
              height: 36,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "white",
              cursor: exporting ? "not-allowed" : "pointer",
              fontSize: 13,
              opacity: exporting ? 0.5 : 1,
            }}
          >
            {exporting ? "…" : "CSV ⬇"}
          </button>
          <button
            onClick={handleDownloadExcel}
            disabled={exporting}
            style={{
              height: 36,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: "white",
              cursor: exporting ? "not-allowed" : "pointer",
              fontSize: 13,
              opacity: exporting ? 0.5 : 1,
            }}
          >
            {exporting ? "…" : "Excel ⬇"}
          </button>
        </div>
      </div>

      {/* ── Status bar ──────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "#fafbfc",
          borderRadius: 10,
          padding: "8px 14px",
          marginBottom: 12,
          border: "1px solid #f1f5f9",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 13,
              color: "#16a34a",
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: loading ? "#ca8a04" : "#16a34a",
                display: "inline-block",
              }}
            />
            {loading ? "Loading..." : "Live"}
          </span>

          {stats && (
            <>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>|</span>
              <span style={{ fontSize: 12, color: "#64748b" }}>
                {stats.total} stocks
              </span>
              {stats.strong_buy > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    background: "#16a34a",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontWeight: 600,
                  }}
                >
                  {stats.strong_buy} STRONG BUY
                </span>
              )}
              {stats.buy > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    background: "#dcfce7",
                    color: "#166534",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontWeight: 600,
                  }}
                >
                  {stats.buy} BUY
                </span>
              )}
              {stats.watch > 0 && (
                <span
                  style={{
                    fontSize: 11,
                    background: "#fef3c7",
                    color: "#92400e",
                    padding: "2px 8px",
                    borderRadius: 4,
                    fontWeight: 600,
                  }}
                >
                  {stats.watch} WATCH
                </span>
              )}
            </>
          )}

          {lastUpdated && (
            <>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>|</span>
              <span style={{ fontSize: 11, color: "#94a3b8" }}>
                {lastUpdated.toLocaleTimeString("en-IN", {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </span>
            </>
          )}
        </div>

        <div style={{ display: "flex", gap: 4 }}>
          {["ALL", "STRONG BUY", "BUY", "WATCH", "SKIP"].map((f) => (
            <button
              key={f}
              onClick={() => setFilterSignal(f)}
              style={{
                padding: "4px 10px",
                borderRadius: 5,
                border: "1px solid",
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
                borderColor: filterSignal === f ? "#3b82f6" : "#e2e8f0",
                background: filterSignal === f ? "#eff6ff" : "white",
                color: filterSignal === f ? "#2563eb" : "#64748b",
              }}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* new custom filter */}
      {/* ── Custom filters ───────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
          padding: "8px 12px",
          marginBottom: 12,
          border: "1px solid #e2e8f0",
          borderRadius: 8,
          background: "#ffffff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          {/* <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span
              style={{
                marginRight: 3,
                fontSize: 11,
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              CHANGE:
            </span>

            {[
              { label: "All", value: "ALL" },
              { label: "≥ 1%", value: "ABOVE_1" },
              { label: "< 1%", value: "BELOW_1" },
            ].map((item) => {
              const active = changeFilter === item.value;

              return (
                <button
                  key={item.value}
                  onClick={() => setChangeFilter(item.value)}
                  style={{
                    padding: "4px 9px",
                    borderRadius: 5,
                    border: "1px solid",
                    borderColor: active ? "#2563eb" : "#e2e8f0",
                    background: active ? "#eff6ff" : "#ffffff",
                    color: active ? "#2563eb" : "#64748b",
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div> */}
          <div
            ref={changeRef}
            style={{ position: "relative", display: "inline-block" }}
          >
            <div
              onClick={() =>
                setOpenFilter(openFilter === "change" ? null : "change")
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                borderRadius: 8,
                cursor: "pointer",
                border: `1px solid ${openFilter === "change" ? "#3b82f6" : "#e2e8f0"}`,
                background: "white",
                fontSize: 13,
              }}
            >
              <span>Price %Change</span>
              <span style={{ color: "#94a3b8" }}>≥</span>
              <span style={{ color: "#2563eb" }}>{changeMin || "—"}</span>
              <span style={{ fontSize: 11 }}>
                {openFilter === "change" ? "▲" : "▼"}
              </span>
            </div>

            {openFilter === "change" && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  width: 300,
                  background: "white",
                  zIndex: 20,
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600 }}>
                    Price %Change
                  </span>
                  <button
                    style={{
                      padding: "3px 12px",
                      borderRadius: 6,
                      border: "1px solid #3b82f6",
                      background: "white",
                      color: "#2563eb",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    Day
                  </button>
                </div>

                <input
                  type="range"
                  min={-20}
                  max={20}
                  step={0.1}
                  value={changeMin === "" ? 0 : Number(changeMin)}
                  onChange={(e) => setChangeMin(e.target.value)}
                  style={{ width: "100%", marginBottom: 16 }}
                />

                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: "#64748b" }}>
                      Min
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={changeMin}
                      onChange={(e) => setChangeMin(e.target.value)}
                      style={{
                        width: "100%",
                        height: 38,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        padding: "6px 10px",
                        fontSize: 13,
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: "#64748b" }}>
                      Max
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={changeMax}
                      placeholder="—"
                      onChange={(e) => setChangeMax(e.target.value)}
                      style={{
                        width: "100%",
                        height: 38,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        padding: "6px 10px",
                        fontSize: 13,
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Volume filters */}
          {/* <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <span
              style={{
                marginRight: 3,
                fontSize: 11,
                fontWeight: 600,
                color: "#64748b",
              }}
            >
              VOLUME:
            </span>

            {[
              { label: "All", value: "ALL" },
              { label: "≥ 1L", value: "ABOVE_1L" },
              { label: "≥ 5L", value: "ABOVE_5L" },
              { label: "≥ 10L", value: "ABOVE_10L" },
            ].map((item) => {
              const active = volumeFilter === item.value;

              return (
                <button
                  key={item.value}
                  onClick={() => setVolumeFilter(item.value)}
                  style={{
                    padding: "4px 9px",
                    borderRadius: 5,
                    border: "1px solid",
                    borderColor: active ? "#16a34a" : "#e2e8f0",
                    background: active ? "#f0fdf4" : "#ffffff",
                    color: active ? "#15803d" : "#64748b",
                    fontSize: 11,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </div> */}
          <div
            ref={volumeRef}
            style={{ position: "relative", display: "inline-block" }}
          >
            <div
              onClick={() =>
                setOpenFilter(openFilter === "volume" ? null : "volume")
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                borderRadius: 8,
                cursor: "pointer",
                border: `1px solid ${openFilter === "volume" ? "#16a34a" : "#e2e8f0"}`,
                background: "white",
                fontSize: 13,
              }}
            >
              <span>Volume</span>
              <span style={{ color: "#94a3b8" }}>≥</span>
              <span style={{ color: "#15803d" }}>
                {volumeMin ? `${volumeMin}L` : "—"}
              </span>
              <span style={{ fontSize: 11 }}>
                {openFilter === "volume" ? "▲" : "▼"}
              </span>
            </div>

            {openFilter === "volume" && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  width: 300,
                  background: "white",
                  zIndex: 20,
                  border: "1px solid #e2e8f0",
                  borderRadius: 12,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  padding: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: 16,
                  }}
                >
                  <span style={{ fontSize: 15, fontWeight: 600 }}>
                    Volume (in lakhs)
                  </span>
                  <button
                    style={{
                      padding: "3px 12px",
                      borderRadius: 6,
                      border: "1px solid #16a34a",
                      background: "white",
                      color: "#15803d",
                      fontSize: 12,
                      cursor: "pointer",
                    }}
                  >
                    Day
                  </button>
                </div>

                <input
                  type="range"
                  min={0}
                  max={50}
                  step={0.5}
                  value={volumeMin === "" ? 0 : Number(volumeMin)}
                  onChange={(e) => setVolumeMin(e.target.value)}
                  style={{ width: "100%", marginBottom: 16 }}
                />

                <div style={{ display: "flex", gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: "#64748b" }}>
                      Min (L)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={volumeMin}
                      onChange={(e) => setVolumeMin(e.target.value)}
                      style={{
                        width: "100%",
                        height: 38,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        padding: "6px 10px",
                        fontSize: 13,
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontSize: 11, color: "#64748b" }}>
                      Max (L)
                    </label>
                    <input
                      type="number"
                      step="0.5"
                      value={volumeMax}
                      placeholder="—"
                      onChange={(e) => setVolumeMax(e.target.value)}
                      style={{
                        width: "100%",
                        height: 38,
                        borderRadius: 8,
                        border: "1px solid #e2e8f0",
                        padding: "6px 10px",
                        fontSize: 13,
                      }}
                    />
                  </div>
                </div>

                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 10 }}>
                  1L = 100,000 shares
                </div>
              </div>
            )}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              color: "#64748b",
            }}
          >
            Showing {sortedData.length} of {data.length}
          </span>

          {(changeFilter !== "ALL" || volumeFilter !== "ALL") && (
            <button
              onClick={() => {
                setChangeFilter("ALL");
                setVolumeFilter("ALL");
              }}
              style={{
                padding: "4px 9px",
                borderRadius: 5,
                border: "1px solid #fecaca",
                background: "#fff",
                color: "#dc2626",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* ── Error state */}
      {error && (
        <div
          style={{
            background: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 13,
            color: "#991b1b",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{error}</span>
          <button
            onClick={refresh}
            style={{
              background: "#991b1b",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              padding: "4px 12px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Data table ──────────────────────────────────────── */}
      <div
        style={{
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          // overflow: "hidden",
          background: "white",
        }}
      >
        <div
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "75vh" }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 12.5,
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#f8fafc",
                  borderBottom: "2px solid #e2e8f0",
                }}
              >
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => handleSort(col.key)}
                    style={{
                      padding: "10px 10px",
                      textAlign: col.align || "left",
                      fontWeight: 600,
                      fontSize: 11,
                      color: "#64748b",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      letterSpacing: 0.3,
                      userSelect: "none",
                      position: "sticky", // ← always sticky
                      top: 0, // ← pin to top
                      left: col.sticky ? 0 : "auto",
                      background: "#f8fafc", // ← always solid bg
                      zIndex: col.sticky ? 3 : 2,
                      // left: col.sticky ? 0 : "auto",
                      // background: col.sticky ? "#f8fafc" : "transparent",
                      // zIndex: col.sticky ? 2 : 0,
                      minWidth: col.width || "auto",
                      borderRight: col.sticky ? "1px solid #e2e8f0" : "none",
                    }}
                  >
                    {/* {col.label}
                    {sortKey === col.key && (
                      <span style={{ marginLeft: 3, fontSize: 9 }}>
                        {sortDir === "asc" ? "▲" : "▼"}
                      </span>
                    )} */}
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent:
                          col.align === "right" ? "flex-end" : "flex-start",
                      }}
                    >
                      {col.label}

                      {col.tooltip && <HeaderTooltip text={col.tooltip} />}

                      {sortKey === col.key && (
                        <span style={{ marginLeft: 3, fontSize: 9 }}>
                          {sortDir === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !data.length ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    style={{
                      padding: 40,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    Loading {selectedIndex} stocks...
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={COLUMNS.length}
                    style={{
                      padding: 40,
                      textAlign: "center",
                      color: "#94a3b8",
                    }}
                  >
                    {filterSignal !== "ALL"
                      ? `No ${filterSignal} signals in ${selectedIndex}`
                      : `No data found for ${selectedIndex}`}
                  </td>
                </tr>
              ) : (
                sortedData.map((row, rowIdx) => (
                  <React.Fragment key={row.symbol}>
                    <tr
                      onClick={() =>
                        setExpandedRow(
                          expandedRow === row.symbol ? null : row.symbol,
                        )
                      }
                      style={{
                        borderBottom: "1px solid #f1f5f9",
                        cursor: "pointer",
                        background:
                          row.momentum_signal === "STRONG BUY"
                            ? "#f0fdf4"
                            : rowIdx % 2 === 0
                              ? "#fff"
                              : "#fafbfc",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f0f9ff";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background =
                          row.momentum_signal === "STRONG BUY"
                            ? "#f0fdf4"
                            : rowIdx % 2 === 0
                              ? "#fff"
                              : "#fafbfc";
                      }}
                    >
                      {COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            padding: "9px 10px",
                            textAlign: col.align || "left",
                            whiteSpace: "nowrap",
                            position: col.sticky ? "sticky" : "static",
                            left: col.sticky ? 0 : "auto",
                            background: "inherit",
                            zIndex: col.sticky ? 1 : 0,
                            borderRight: col.sticky
                              ? "1px solid #f1f5f9"
                              : "none",
                          }}
                        >
                          {renderCell(col, row)}
                        </td>
                      ))}
                    </tr>

                    {/* ── Expanded detail row ──────────────── */}
                    {expandedRow === row.symbol && (
                      <tr>
                        <td
                          colSpan={COLUMNS.length}
                          style={{
                            padding: "14px 16px",
                            background: "#f8fafc",
                            borderBottom: "2px solid #e2e8f0",
                          }}
                        >
                          <div
                            onClick={() => openStockDetail(row)}
                            title={
                              row.security_id
                                ? `Click to open ${row.symbol} details in new tab`
                                : "Details unavailable"
                            }
                            style={{
                              display: "flex",
                              gap: 24,
                              alignItems: "flex-start",
                              flexWrap: "wrap",
                              cursor: row.security_id ? "pointer" : "default",
                            }}
                          >
                            {/* Filter check */}
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  marginBottom: 6,
                                  fontWeight: 600,
                                }}
                              >
                                FILTER CHECK
                              </div>
                              <FilterBadges filters={row.momentum_filters} />
                            </div>

                            {/* Price data */}
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  marginBottom: 6,
                                  fontWeight: 600,
                                }}
                              >
                                PRICE
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#475569",
                                  lineHeight: 1.8,
                                }}
                              >
                                O: {formatPrice(row.open)} H:{" "}
                                {formatPrice(row.high)} L:{" "}
                                {formatPrice(row.low)}
                                <br />
                                52W: {formatPrice(row.low_52w)} —{" "}
                                {formatPrice(row.high_52w)}
                                <br />
                                EMA50: {formatPrice(row.ema_50)} SMA200:{" "}
                                {formatPrice(row.sma_200)}
                              </div>
                            </div>

                            {/* Signals */}
                            <div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  marginBottom: 6,
                                  fontWeight: 600,
                                }}
                              >
                                SIGNALS
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                }}
                              >
                                <SignalBadge value={row.ut_signal} />
                                <span
                                  style={{ fontSize: 10, color: "#94a3b8" }}
                                >
                                  UT
                                </span>
                                <SignalBadge value={row.bbawe_signal} />
                                <span
                                  style={{ fontSize: 10, color: "#94a3b8" }}
                                >
                                  BBAWE
                                </span>
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 600,
                                    padding: "2px 6px",
                                    borderRadius: 4,
                                    background:
                                      row.smart_money === "Accumulation"
                                        ? "#dcfce7"
                                        : row.smart_money === "Distribution"
                                          ? "#fee2e2"
                                          : "#f1f5f9",
                                    color:
                                      row.smart_money === "Accumulation"
                                        ? "#166534"
                                        : row.smart_money === "Distribution"
                                          ? "#991b1b"
                                          : "#64748b",
                                  }}
                                >
                                  {row.smart_money || "—"}
                                </span>
                                <span
                                  style={{ fontSize: 10, color: "#94a3b8" }}
                                >
                                  Smart $
                                </span>
                              </div>
                            </div>

                            {/* Score */}
                            <div
                              style={{ marginLeft: "auto", textAlign: "right" }}
                            >
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  marginBottom: 4,
                                  fontWeight: 600,
                                }}
                              >
                                MOMENTUM
                              </div>
                              <div
                                style={{
                                  fontSize: 28,
                                  fontWeight: 700,
                                  color:
                                    row.momentum_score >= 70
                                      ? "#16a34a"
                                      : row.momentum_score >= 45
                                        ? "#ca8a04"
                                        : "#dc2626",
                                }}
                              >
                                {row.momentum_score}
                              </div>
                              <div style={{ fontSize: 10, color: "#94a3b8" }}>
                                / 100
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Scoring legend ──────────────────────────────────── */}
      <div
        style={{
          marginTop: 12,
          padding: "10px 14px",
          background: "#f8fafc",
          borderRadius: 8,
          border: "1px solid #f1f5f9",
          fontSize: 11,
          color: "#94a3b8",
          lineHeight: 1.8,
        }}
      >
        <span style={{ color: "#64748b", fontWeight: 600 }}>Scoring: </span>
        RSI ≥ 60 (+25) | RVOL ≥ 2× (+20) | Within 10% of 52W High (+20) | Above
        EMA20 (+10) | MACD +ve (+10) | VWAP Bull (+5) | UT Bot (+5) | BBAWE (+5)
        <span style={{ margin: "0 6px" }}>|</span>
        <span style={{ color: "#16a34a", fontWeight: 600 }}>STRONG BUY</span> =
        All 3 core pass |{" "}
        <span style={{ color: "#166534", fontWeight: 600 }}>BUY</span> = ≥65 |{" "}
        <span style={{ color: "#92400e", fontWeight: 600 }}>WATCH</span> = ≥45 |{" "}
        <span style={{ color: "#94a3b8", fontWeight: 600 }}>SKIP</span> = below
      </div>
    </div>
  );
};

export default MomentumScreener;
